import { fetchHTML } from './utils-fetch-html.js';
import { d as debounce } from './debounce-CFtxQv9K.js';

class PredictiveSearchResults extends HTMLElement {
  abortController;

  connectedCallback() {
    this.options = {
      minimumSearchTermLength: 1,
      searchId: this.dataset.searchId,
      hideElement: this.dataset.hideElement,
    };

    // Get the search form
    this.search = document.querySelector(`#${this.options.searchId}`);
    if (!this.search) return;

    if (this.options.hideElement) {
      this.elementsToHide = document.querySelector(`#${this.options.hideElement}`);
    }

    // Get the predictive search controller (button in header)
    this.predictiveSearchControllerText = document.querySelector(
      '[data-predictive-search-controller-text]'
    );

    // Handle search input
    this.unsubscribeSearchInput = subscribe(
      `search:input:${this.dataset.searchId}`,
      ({ value }) => {
        this.enableLoaders();
        this.updateSearch(value);
        //set button text to the search term
        this.predictiveSearchControllerText.textContent = value;
      }
    );

    // Handle predictive search suggestion click
    const predictiveSearchSuggestions = document.querySelectorAll(
      '[data-predictive-search-suggestion]'
    );

    predictiveSearchSuggestions.forEach((suggestion) => {
      suggestion.addEventListener('click', (event) => {
        const suggestionValue = event.target.dataset.predictiveSearchSuggestion;
        // get the input element by name
        const inputElement = this.search.querySelector(`input[name="q"]`);
        // set input value to the suggestion
        inputElement.value = suggestionValue;
        document.querySelector('[data-search-clear]').ariaHidden = length === 0;

        // Update results
        this.updateSearch(suggestionValue);
        // set button text to the search term
        this.predictiveSearchControllerText.textContent = suggestionValue;
      });
    });

    // Handle search clear button
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.search.clearSearchInput();
      }
    });

    // Handle search clear from search component
    this.unsubscribeSearchClear = subscribe(`search:clear:${this.dataset.searchId}`, () => {
      this.onClearSearchInput();
    });

    // When rendered in a drawer, subscribe to the drawer open-event to clear the input and results
    this.drawerId = this.closest('drawer-element')?.id;
    if (this.drawerId) {
      this.unsubscribeDrawerOpened = subscribe(`drawer:opened:${this.drawerId}`, () => {
        const inputElement = this.search.querySelector(`input[name="q"]`);
        const value = this.search.querySelector(`input[name="q"]`).value;
        const length = inputElement.value.length;
        // this.search.clearSearchInput();
        // this.clearSearchResults();
        //If input is not empty, get results for the input
        if (value) {
          this.updateSearch(value);
          document.querySelector('[data-search-clear]').ariaHidden = length === 0;
          inputElement.focus();
          inputElement.setSelectionRange(length, length);
        } else {
          inputElement.focus();
        }
      });
    }

    this.unsubscribeMachineChange = subscribe('machine:change', () => {
      this.syncViewAllResultsUrl();
    });

    this.syncViewAllResultsUrl();
  }

  disconnectedCallback() {
    this.unsubscribeSearchInput();
    this.unsubscribeSearchClear();
    if (this.drawerId) {
      this.unsubscribeDrawerOpened();
    }
    this.unsubscribeMachineChange?.();
  }

  /**
   * Clear the search results
   */
  clearSearchResults() {
    this.innerHTML = '';
  }

  /**
   * Clear the search input
   */
  onClearSearchInput() {
    this.ariaHidden = true;
    this.hideOnSearchResults(false);
  }

  /**
   * Enable loaders
   */
  enableLoaders() {
    this.querySelectorAll('[data-loader]').forEach((loader) => {
      loader.ariaHidden = false;
    });
  }

  /**
   * Disable loaders
   */
  disableLoaders() {
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.ariaHidden = true;
      });
    }, 50);
  }

  /**
   * Update the search
   */
  updateSearch = debounce((searchTerm) => this.getSearchResults(searchTerm), 300);

  /**
   * Get the search results
   */
  getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.options.minimumSearchTermLength) {
      this.clearSearchResults();
      this.hideOnSearchResults(false);
      this.ariaHidden = true;
      return;
    }
    this.fetchSearchResults(searchTerm);
  }

  /**
   * Hide other elements when the predictive search results are shown
   */
  hideOnSearchResults(hide = true) {
    if (this.elementsToHide) this.elementsToHide.ariaHidden = hide;
  }

  /**
   * Fetch the search results
   */
  async fetchSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const searchUrl = routes.predictive_search_url;
      const html = await fetchHTML(
        `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search`,
        { signal: this.abortController.signal }
      );
      const resultsMarkup = html.querySelector('predictive-search-results').innerHTML;
      this.innerHTML = '';
      this.innerHTML = resultsMarkup;
      this.syncViewAllResultsUrl();
      this.ariaHidden = false;
      this.hideOnSearchResults();
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      this.disableLoaders();
    }
  }

  /**
   * Ensure the "View all results" button carries the active machine filter.
   */
  syncViewAllResultsUrl() {
    const viewAllLink = this.querySelector('[data-view-all-results]');
    if (!viewAllLink) return;

    if (!viewAllLink.dataset.baseHref) {
      viewAllLink.dataset.baseHref = viewAllLink.getAttribute('href');
    }

    const machineController = window.getMachineController?.();
    const machineData = machineController?.getCurrentState?.();
    const machineParam = 'filter.p.m.custom.models';
    const baseHref = viewAllLink.dataset.baseHref;
    const url = new URL(baseHref, window.location.origin);

    if (machineData?.modelGid) {
      url.searchParams.set(machineParam, machineData.modelGid);
    } else {
      url.searchParams.delete(machineParam);
    }

    const relativeUrl = `${url.pathname}${url.search}${url.hash}`;
    viewAllLink.setAttribute('href', relativeUrl);
  }
}

customElements.define('predictive-search-results', PredictiveSearchResults);
